import QRCode from "qrcode.react";
import React, { useEffect, useState, useRef } from "react";
import { IoCopyOutline, IoRadioOutline, IoWalletOutline, IoShieldCheckmarkOutline, IoSyncOutline, IoCheckmarkCircleOutline } from "react-icons/io5";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import PayHeader from "../../components/auth/PayHeader";
import { Loader } from "../../components/Loader";
import API from "../../helper/AxiosInterceptor";
import { TRON_CHAIN_ID } from "../../helper/constant";
import { getNetwork, getTronNetwork } from "../../helper/evmHelper";
import { copyToClipboard, formatDate } from "../../helper/helper";
import copy from "copy-to-clipboard";
import { toast } from "react-toastify";
import "../../../src/i18n/i18n"; // ensure i18n is loaded

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "tr", label: "Turkish" },
];

const PaymentInformation = () => {
  const { paymentId } = useParams();
  const { t, i18n } = useTranslation();
  const [paymentLink, setPaymentLink] = useState(null);
  const [timeLeft, setTimeLeft] = useState("");
  const [network, setNetwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(localStorage.getItem("payment_lang") || "en");

  const handleLanguageChange = (code) => {
    setLang(code);
    i18n.changeLanguage(code);
    localStorage.setItem("payment_lang", code);
    // RTL for Arabic
    document.documentElement.setAttribute("dir", code === "ar" ? "rtl" : "ltr");
  };

  // Apply RTL on mount if Arabic was last selected
  useEffect(() => {
    document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
    return () => document.documentElement.setAttribute("dir", "ltr");
  }, []);

  const fetchPaymentLink = async () => {
    try {
      if (paymentId === "preview") {
        const storedData = localStorage.getItem("preview_payment_data");
        if (storedData) {
          const parsedData = JSON.parse(storedData);
          setPaymentLink(parsedData);
          setNetwork({ network: "Ethereum", tokenType: "ERC20" });
        }
        setLoading(false);
        return;
      }

      const resp = await API.get(
        `/payment-link/get-tx-status`,
        { params: { paymentId } }
      );
      setPaymentLink(resp?.data?.data);
      if (resp?.data?.data?.chainId === TRON_CHAIN_ID) {
        const net = await getTronNetwork();
        setNetwork(net);
      } else {
        const net = await getNetwork(resp?.data?.data?.chainId);
        setNetwork(net);
      }

      if (resp?.data?.data?.status === "SUCCESS") {
        Swal.fire({
          position: "center",
          icon: "success",
          title: t("payment_received_title"),
          text: t("payment_received_text"),
          showConfirmButton: false,
          timer: 2000,
        });
        // Redirect to merchant's URL after success with all transaction parameters
        if (resp?.data?.data?.redirectUrl) {
          setTimeout(() => {
            const d = resp.data.data;
            const url = new URL(d.redirectUrl);
            // Core transaction info
            url.searchParams.set('transactionId', paymentId);
            url.searchParams.set('status', d.status || 'SUCCESS');
            url.searchParams.set('currency', d.code || '');
            url.searchParams.set('symbol', d.symbol || '');
            url.searchParams.set('amount', d.amount || '');
            url.searchParams.set('receivedAmount', d.recivedAmount || '');
            // Network & blockchain info
            url.searchParams.set('chainId', d.chainId || '');
            if (d.hash) url.searchParams.set('hash', d.hash);
            if (d.fromAddress) url.searchParams.set('fromAddress', d.fromAddress);
            // Fiat-specific fields
            if (d.transactionType) url.searchParams.set('transactionType', d.transactionType);
            if (d.fiatCurrency) url.searchParams.set('fiatCurrency', d.fiatCurrency);
            if (d.fiatAmount) url.searchParams.set('fiatAmount', d.fiatAmount);
            if (d.pricePerCoin) url.searchParams.set('pricePerCoin', d.pricePerCoin);
            if (d.cryptoAmount) url.searchParams.set('cryptoAmount', d.cryptoAmount);
            // Metadata — flatten into query params
            if (d.metadata && typeof d.metadata === 'object') {
              Object.entries(d.metadata).forEach(([key, value]) => {
                url.searchParams.set(`metadata_${key}`, typeof value === 'object' ? JSON.stringify(value) : String(value));
              });
            }
            window.location.href = url.toString();
          }, 3000);
        }
      }
    } catch (error) {
      console.error("Error in transactions list", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTimeLeft = () => {
    if (paymentLink?.expireTime) {
      const expireTime = new Date(paymentLink.expireTime * 1000);
      const currentTime = new Date();
      const difference = expireTime - currentTime;

      if (difference > 0) {
        const hours = Math.floor(difference / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        setTimeLeft(`${hours}h ${minutes}m ${seconds}s`);
      } else {
        setTimeLeft("Expired");
      }
    }
  };

  useEffect(() => { fetchPaymentLink(); }, []);

  const pollingRef = useRef(null);
  const hasSuccessShownRef = useRef(false);

  useEffect(() => {
    if (paymentId === "preview" || loading) return;

    pollingRef.current = setInterval(async () => {
      try {
        const resp = await API.get(
          `/payment-link/get-tx-status`,
          { params: { paymentId } }
        );
        const newData = resp?.data?.data;
        if (newData) {
          setPaymentLink(newData);
          if (newData.status === "SUCCESS" && !hasSuccessShownRef.current) {
            hasSuccessShownRef.current = true;
            clearInterval(pollingRef.current);
            Swal.fire({
              position: "center",
              icon: "success",
              title: t("payment_received_title"),
              text: t("payment_received_text"),
              showConfirmButton: false,
              timer: 2000,
            });
            // Redirect to merchant's URL after success with all transaction parameters
            if (newData.redirectUrl) {
              setTimeout(() => {
                const url = new URL(newData.redirectUrl);
                // Core transaction info
                url.searchParams.set('transactionId', paymentId);
                url.searchParams.set('status', newData.status || 'SUCCESS');
                url.searchParams.set('currency', newData.code || '');
                url.searchParams.set('symbol', newData.symbol || '');
                url.searchParams.set('amount', newData.amount || '');
                url.searchParams.set('receivedAmount', newData.recivedAmount || '');
                // Network & blockchain info
                url.searchParams.set('chainId', newData.chainId || '');
                if (newData.hash) url.searchParams.set('hash', newData.hash);
                if (newData.fromAddress) url.searchParams.set('fromAddress', newData.fromAddress);
                // Fiat-specific fields
                if (newData.transactionType) url.searchParams.set('transactionType', newData.transactionType);
                if (newData.fiatCurrency) url.searchParams.set('fiatCurrency', newData.fiatCurrency);
                if (newData.fiatAmount) url.searchParams.set('fiatAmount', newData.fiatAmount);
                if (newData.pricePerCoin) url.searchParams.set('pricePerCoin', newData.pricePerCoin);
                if (newData.cryptoAmount) url.searchParams.set('cryptoAmount', newData.cryptoAmount);
                // Metadata — flatten into query params
                if (newData.metadata && typeof newData.metadata === 'object') {
                  Object.entries(newData.metadata).forEach(([key, value]) => {
                    url.searchParams.set(`metadata_${key}`, typeof value === 'object' ? JSON.stringify(value) : String(value));
                  });
                }
                window.location.href = url.toString();
              }, 3000);
            }
          }
          if (newData.status?.toUpperCase() === "EXPIRED") {
            clearInterval(pollingRef.current);
          }
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }, 5000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [paymentId, loading]);

  useEffect(() => {
    if (paymentLink) {
      calculateTimeLeft();
      const timer = setInterval(() => {
        if (timeLeft !== "Expired") calculateTimeLeft();
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [paymentLink, timeLeft]);

  const getPaymentURI = () => paymentLink?.toAddress || "";

  useEffect(() => {
    if (paymentLink?.theme === "DARK") {
      document.body.classList.add("dark-theme");
    } else {
      document.body.classList.remove("dark-theme");
    }
    return () => document.body.classList.remove("dark-theme");
  }, [paymentLink?.theme]);

  let logoUrl = null;
  if (paymentId === "preview" && paymentLink?.logo && paymentLink.logo.startsWith("data:image")) {
    logoUrl = paymentLink.logo;
  } else if (paymentId === "preview" && paymentLink?.logo) {
    if (paymentLink.logo.startsWith("http") || paymentLink.logo.startsWith("blob:")) {
      logoUrl = paymentLink.logo;
    } else {
      logoUrl = `${API.defaults.baseURL}/${paymentLink.logo}`;
    }
  } else {
    logoUrl = paymentLink?.logo ? `${API.defaults.baseURL}/${paymentLink.logo}` : null;
  }

  const themeClass = paymentLink?.theme === "DARK" ? "dark-theme" : "";
  const isTestnet = process.env.REACT_APP_NETWORK_MODE === 'testnet';

  if (loading) return <Loader />;

  const copyBtnStyle = { border: "none", background: "transparent", cursor: "pointer", padding: 0 };
  const amountRowStyle = { display: "flex", alignItems: "center", gap: "8px" };

  return (
    <div className={themeClass}>
      {/* ── Inline CSS Animations for Processing Effects ── */}
      <style>{`
        @keyframes pcz-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.85; }
        }
        @keyframes pcz-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pcz-dots {
          0%, 20% { content: '.'; }
          40% { content: '..'; }
          60%, 100% { content: '...'; }
        }
        @keyframes pcz-progress-bar {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        @keyframes pcz-scan-line {
          0% { top: 0%; }
          50% { top: 90%; }
          100% { top: 0%; }
        }
        @keyframes pcz-fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pcz-checkmark-pop {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes pcz-ripple {
          0% { box-shadow: 0 0 0 0 rgba(245, 192, 5, 0.4); }
          70% { box-shadow: 0 0 0 15px rgba(245, 192, 5, 0); }
          100% { box-shadow: 0 0 0 0 rgba(245, 192, 5, 0); }
        }
        @keyframes pcz-shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes pcz-bounce-dot {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
        .pcz-step-tracker {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0;
          padding: 20px 10px;
          animation: pcz-fade-in 0.5s ease;
        }
        .pcz-step {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 1;
          min-width: 70px;
        }
        .pcz-step-icon {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          position: relative;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08), inset 0 2px 4px rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(4px);
        }
        .pcz-step-icon.active {
          animation: pcz-ripple 1.5s infinite;
        }
        .pcz-step-icon.completed {
          animation: pcz-checkmark-pop 0.4s ease forwards;
        }
        .pcz-step-label {
          font-size: 10px;
          margin-top: 6px;
          font-weight: 600;
          text-align: center;
          max-width: 80px;
          line-height: 1.2;
        }
        .pcz-step-line {
          height: 3px;
          width: 50px;
          border-radius: 3px;
          margin-bottom: 20px;
          position: relative;
          overflow: hidden;
        }
        .pcz-step-line.active::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          width: 100%;
          background: linear-gradient(90deg, transparent, #f5c005, transparent);
          background-size: 200% 100%;
          animation: pcz-shimmer 1.5s infinite;
        }
        .pcz-scan-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          pointer-events: none;
          overflow: hidden;
          border-radius: 8px;
        }
        .pcz-scan-overlay::after {
          content: '';
          position: absolute;
          left: 5%;
          width: 90%;
          height: 3px;
          background: linear-gradient(90deg, transparent, #f5c005, transparent);
          animation: pcz-scan-line 2s ease-in-out infinite;
          box-shadow: 0 0 12px rgba(245, 192, 5, 0.5);
        }
        .pcz-processing-banner {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 14px 20px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 14px;
          animation: pcz-fade-in 0.5s ease;
          margin: 12px 0;
        }
        .pcz-bounce-dots {
          display: inline-flex;
          gap: 4px;
          align-items: center;
        }
        .pcz-bounce-dots span {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          display: inline-block;
          animation: pcz-bounce-dot 1.4s infinite ease-in-out both;
        }
        .pcz-bounce-dots span:nth-child(1) { animation-delay: -0.32s; }
        .pcz-bounce-dots span:nth-child(2) { animation-delay: -0.16s; }
        .pcz-bounce-dots span:nth-child(3) { animation-delay: 0; }
        .pcz-spinner {
          width: 18px;
          height: 18px;
          border: 3px solid transparent;
          border-top-color: currentColor;
          border-radius: 50%;
          animation: pcz-spin 0.8s linear infinite;
          flex-shrink: 0;
        }
      `}</style>
      {isTestnet && (
        <div style={{
          background: 'rgba(255, 223, 100, 0.85)',
          backdropFilter: 'blur(4px)',
          color: '#000',
          textAlign: 'center',
          padding: '8px',
          fontSize: '14px',
          fontWeight: 'bold',
          width: '100%',
          position: 'relative',
          zIndex: 9999,
          borderBottom: '1px solid rgba(224, 168, 0, 0.5)'
        }}>
          ⚠️ SANDBOX INVOICE: You are viewing a test payment link. Do not send real funds to this address!
        </div>
      )}
      <PayHeader logo={logoUrl} theme={paymentLink?.theme} />
      <div className="site-bg">
        <div className="inner-page-warper section-padding pay-bg">
          <div className="container">

            <div className="payment-information-bg">
              <div className="white-bg padd20">
                {/* ── Card Header: Title + Language Dropdown ── */}
                <div style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "16px",
                  minHeight: "40px",
                }}>
                  <h2 style={{ margin: 0, textAlign: "center" }}>{t("payment_information")}</h2>

                  {/* Language Dropdown — pinned to right */}
                  <div style={{ position: "absolute", right: 0, display: "flex", alignItems: "center" }}>
                    <select
                      value={lang}
                      onChange={(e) => handleLanguageChange(e.target.value)}
                      style={{
                        padding: "6px 10px",
                        borderRadius: "8px",
                        border: "1.5px solid #f5c005",
                        background: themeClass === "dark-theme" ? "#1a1a1a" : "#fff",
                        color: themeClass === "dark-theme" ? "#f5c005" : "#333",
                        fontWeight: "600",
                        fontSize: "13px",
                        cursor: "pointer",
                        outline: "none",
                        boxShadow: "0 2px 8px rgba(245,192,5,0.25)",
                        transition: "box-shadow 0.2s ease",
                        minWidth: "120px",
                      }}
                    >
                      {LANGUAGES.map((lng) => (
                        <option
                          key={lng.code}
                          value={lng.code}
                          style={{
                            background: themeClass === "dark-theme" ? "#1a1a1a" : "#fff",
                            color: themeClass === "dark-theme" ? "#f5c005" : "#333",
                          }}
                        >
                          {lng.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <ul className="payment-information">

                  <li>
                    <div className="payment-left">{t("payment_id")}</div>
                    <div className="payment-right">{paymentId}</div>
                  </li>

                  <li>
                    <div className="payment-left">{t("crypto_code")}</div>
                    <div className="payment-right">{paymentLink?.code}</div>
                  </li>

                  {paymentLink?.transactionType && (
                    <li>
                      <div className="payment-left">{t("transaction_type")}</div>
                      <div className="payment-right">{paymentLink?.transactionType}</div>
                    </li>
                  )}

                  {paymentLink?.fiatCurrency && (
                    <li>
                      <div className="payment-left">{t("fiat_currency")}</div>
                      <div className="payment-right">{paymentLink?.fiatCurrency?.toUpperCase()}</div>
                    </li>
                  )}

                  {paymentLink?.fiatAmount && (
                    <li>
                      <div className="payment-left">{t("fiat_amount")}</div>
                      <div className="payment-right">
                        {parseFloat(paymentLink.fiatAmount).toLocaleString()} {paymentLink?.fiatCurrency?.toUpperCase()}
                      </div>
                    </li>
                  )}



                  {paymentLink?.cryptoAmount && (
                    <li>
                      <div className="payment-left">{t("crypto_amount")}</div>
                      <div className="payment-right" style={amountRowStyle}>
                        {paymentLink?.cryptoAmount}
                        <button
                          onClick={() => { copy(String(paymentLink?.cryptoAmount)); toast.success(t("copied_amount")); }}
                          title={t("crypto_amount")}
                          style={copyBtnStyle}
                        >
                          <IoCopyOutline style={{ fontSize: "16px", color: paymentLink?.theme === "DARK" ? "#ccc" : "#888" }} />
                        </button>
                      </div>
                    </li>
                  )}

                  {paymentLink?.pricePerCoin && (
                    <li>
                      <div className="payment-left">{t("price_per_coin")}</div>
                      <div className="payment-right">{paymentLink?.pricePerCoin}</div>
                    </li>
                  )}

                  <li>
                    <div className="payment-left" style={{ fontWeight: "bold" }}>{t("total_amount")}</div>
                    <div className="payment-right" style={{ ...amountRowStyle, fontWeight: "bold", fontSize: "1.05em" }}>
                      {paymentLink?.amount} {paymentLink?.symbol}
                      <button
                        onClick={() => { copy(String(paymentLink?.amount)); toast.success(t("copied_total")); }}
                        title={t("total_amount")}
                        style={copyBtnStyle}
                      >
                        <IoCopyOutline style={{ fontSize: "16px", color: "#888" }} />
                      </button>
                    </div>
                  </li>

                  <li>
                    <div className="payment-left">{t("received_so_far")}</div>
                    <div className="payment-right">
                      {paymentLink?.recivedAmount ?? 0} {paymentLink?.symbol}
                    </div>
                  </li>

                  <li>
                    <div style={{
                      margin: "0 auto",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      borderRadius: "50%",
                      flexDirection: "column",
                    }}>
                      {timeLeft === "Expired" ? (
                        <div style={{ color: "red", marginTop: "20px" }}>
                          {t("payment_link_expired")}
                        </div>
                      ) : (
                        <div style={{
                          display: "flex", justifyContent: "center", alignItems: "center",
                          textAlign: "center", marginTop: "20px", marginBottom: "20px", fontSize: "14px",
                        }}>
                          {t("qr_instruction")}
                        </div>
                      )}

                      {paymentLink?.status?.toUpperCase() === "PENDING FROM BUYER SIDE" && paymentLink?.recivedAmount > 0 && (
                        <div style={{
                          backgroundColor: "#fff4e5", color: "#ff9800", border: "1px solid #ff9800",
                          padding: "15px", borderRadius: "8px", marginTop: "15px", marginBottom: "15px",
                          fontWeight: "bold", textAlign: "center", fontSize: "15px", maxWidth: "400px"
                        }}>
                          {t("you_paid", { amount: paymentLink?.recivedAmount, symbol: paymentLink?.symbol })}
                          <br />
                          {t("pay_remaining", {
                            remaining: (parseFloat(paymentLink?.amount) - parseFloat(paymentLink?.recivedAmount)).toFixed(6).replace(/\.?0+$/, ''),
                            symbol: paymentLink?.symbol
                          })}
                        </div>
                      )}

                      {paymentLink?.status?.toUpperCase() === "PENDING FROM BUYER SIDE" && (
                        <div className="payment-QR" style={{ position: 'relative' }}>
                          <QRCode value={getPaymentURI()} />
                          <div className="pcz-scan-overlay"></div>
                        </div>
                      )}

                      <div style={{
                        display: "flex", justifyContent: "center", alignItems: "center",
                        textAlign: "center", marginTop: "20px", marginBottom: "20px",
                        color: paymentLink?.recivedAmount > 0 ? "#20cd50" : "red",
                        fontSize: "12px",
                      }}>
                        {timeLeft.toLowerCase() === "expired"
                          ? t("network_expired", {
                              received: paymentLink?.recivedAmount ?? 0,
                              symbol: paymentLink?.symbol,
                              network: network?.network,
                              tokenType: network?.tokenType ? `(${network.tokenType})` : "",
                            })
                          : t("network_warning", {
                              amount: paymentLink?.amount,
                              symbol: paymentLink?.symbol,
                              network: network?.network,
                              tokenType: network?.tokenType ? `(${network.tokenType})` : "",
                            })}
                      </div>
                    </div>
                  </li>

                  {paymentLink?.status.toUpperCase() === "PENDING FROM BUYER SIDE" && (
                    <>
                      <li>
                        <div className="payment-left">{t("send_to_address")}</div>
                        <div className="payment-right">
                          {paymentLink?.toAddress}
                          <button
                            onClick={() => { copy(paymentLink?.toAddress); toast.success(t("copied_address")); }}
                            style={{ marginLeft: "1rem", border: "none", background: "transparent", cursor: "pointer" }}
                          >
                            <IoCopyOutline style={{ fontSize: "16px", color: paymentLink?.theme === "DARK" ? "#ccc" : "#888" }} />
                          </button>
                        </div>
                      </li>
                      <li>
                        <div className="payment-left">{t("time_left")}</div>
                        <div className={`payment-right status ${timeLeft.toLowerCase() !== "expired" ? "success" : "error"}`}>
                          {timeLeft}
                        </div>
                      </li>
                    </>
                  )}

                  <li>
                    <div className="payment-left">{t("buyer_email")}</div>
                    <div className="payment-right">{paymentLink?.buyerEmail}</div>
                  </li>

                  {/* Metadata is internal tracking data for merchants only — not shown to buyers */}

                  <li>
                    <div className="payment-left">{t("expiry_time")}</div>
                    <div className="payment-right">{formatDate(paymentLink?.expireTime * 1000)}</div>
                  </li>

                  <li>
                    <div className="payment-left">{t("created_at")}</div>
                    <div className="payment-right">{formatDate(paymentLink?.createdAt)}</div>
                  </li>

                  {/* ── Animated Processing Status Tracker ── */}
                  {paymentLink?.status?.toUpperCase() !== "EXPIRED" && (
                    <li>
                      <div style={{ width: '100%', padding: '10px 0' }}>
                        {(() => {
                          const status = paymentLink?.status?.toUpperCase();
                          const isDark = themeClass === 'dark-theme';
                          const bgBase = isDark ? '#1a1a1a' : '#f8f9fa';
                          const textMuted = isDark ? '#888' : '#999';
                          const textActive = isDark ? '#f5c005' : '#f5a623';
                          const textDone = '#4caf50';
                          const bgDone = 'rgba(76,175,80,0.15)';
                          const bgActive = isDark ? 'rgba(245,192,5,0.15)' : 'rgba(245,166,35,0.12)';
                          const bgPending = isDark ? '#2a2a2a' : '#e9ecef';

                          // Determine current step
                          let currentStep = 0;
                          if (status === 'PENDING FROM BUYER SIDE' && (paymentLink?.recivedAmount ?? 0) == 0) currentStep = 0;
                          else if (status === 'PENDING FROM BUYER SIDE' && paymentLink?.recivedAmount > 0) currentStep = 1;
                          else if (status === 'PENDING') currentStep = 2;
                          else if (status === 'PARTIALLY_SUCCESS' || status === 'PARTIALLY SUCCESS') currentStep = 3;
                          else if (status === 'SUCCESS') currentStep = 5;

                          const steps = [
                            { icon: <IoRadioOutline />, label: t('step_awaiting_payment') },
                            { icon: <IoWalletOutline />, label: t('step_payment_detected') },
                            { icon: <IoShieldCheckmarkOutline />, label: currentStep > 2 ? 'Verified' : t('step_verifying') },
                            { icon: <IoSyncOutline />, label: currentStep > 3 ? 'Transferred' : t('step_transferring') },
                            { icon: <IoCheckmarkCircleOutline />, label: currentStep > 4 ? 'Completed' : t('step_complete') },
                          ];

                          const getStepStyle = (idx) => {
                            if (idx < currentStep) return { bg: bgDone, border: textDone, color: textDone };
                            if (idx === currentStep) return { bg: bgActive, border: textActive, color: textActive };
                            return { bg: bgPending, border: 'transparent', color: textMuted };
                          };

                          const getLineStyle = (idx) => {
                            if (idx < currentStep) return { bg: textDone };
                            if (idx === currentStep) return { bg: textActive, active: true };
                            return { bg: isDark ? '#333' : '#ddd' };
                          };

                          // Banner messages per step
                          const bannerMessages = [
                            { text: t('banner_scanning'), bg: isDark ? 'rgba(245,192,5,0.08)' : 'rgba(245,192,5,0.08)', color: textActive },
                            { text: t('banner_detected'), bg: isDark ? 'rgba(76,175,80,0.08)' : 'rgba(76,175,80,0.08)', color: '#66bb6a' },
                            { text: t('banner_verifying'), bg: isDark ? 'rgba(33,150,243,0.08)' : 'rgba(33,150,243,0.08)', color: '#42a5f5' },
                            { text: t('banner_transferring'), bg: isDark ? 'rgba(171,71,188,0.08)' : 'rgba(171,71,188,0.08)', color: '#ab47bc' },
                            { text: t('banner_complete'), bg: bgDone, color: textDone },
                          ];

                          const banner = bannerMessages[currentStep];

                          return (
                            <>
                              {/* Step Tracker */}
                              <div className="pcz-step-tracker">
                                {steps.map((step, idx) => (
                                  <React.Fragment key={idx}>
                                    <div className="pcz-step">
                                      <div
                                        className={`pcz-step-icon ${idx === currentStep ? 'active' : ''} ${idx < currentStep ? 'completed' : ''}`}
                                        style={{
                                          background: getStepStyle(idx).bg,
                                          border: `2px solid ${getStepStyle(idx).border}`,
                                          color: getStepStyle(idx).color,
                                          fontSize: idx < currentStep ? '22px' : '20px',
                                          textShadow: idx === currentStep ? `0 0 10px ${getStepStyle(idx).color}88` : 'none',
                                        }}
                                      >
                                        {idx < currentStep ? <IoCheckmarkCircleOutline /> : step.icon}
                                      </div>
                                      <div className="pcz-step-label" style={{ color: getStepStyle(idx).color }}>
                                        {step.label}
                                      </div>
                                    </div>
                                    {idx < steps.length - 1 && (
                                      <div
                                        className={`pcz-step-line ${getLineStyle(idx).active ? 'active' : ''}`}
                                        style={{ background: getLineStyle(idx).bg }}
                                      />
                                    )}
                                  </React.Fragment>
                                ))}
                              </div>

                              {/* Processing Banner */}
                              {currentStep < 4 && (
                                <div
                                  className="pcz-processing-banner"
                                  style={{ background: banner.bg, color: banner.color, border: `1px solid ${banner.color}22` }}
                                >
                                  <div className="pcz-spinner" />
                                  <span>{banner.text}</span>
                                  <div className="pcz-bounce-dots">
                                    <span style={{ background: banner.color }} />
                                    <span style={{ background: banner.color }} />
                                    <span style={{ background: banner.color }} />
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </li>
                  )}

                  <li>
                    <div className="payment-left">{t("payment_status")}</div>
                    <div className={`status ${
                      paymentLink?.status.toUpperCase() === "SUCCESS" ? "success"
                      : paymentLink?.status.toUpperCase() === "EXPIRED" ? "error"
                      : "pending"
                    }`}>
                      {paymentLink?.status}
                    </div>
                  </li>

                  {paymentLink?.status?.toUpperCase() === "SUCCESS" && paymentLink?.redirectUrl && (
                    <li>
                      <div style={{
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                        border: '1px solid #4caf50',
                        padding: '15px',
                        borderRadius: '8px',
                        marginTop: '10px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        fontSize: '14px',
                        width: '100%'
                      }}>
                        ✅ Payment successful! Redirecting you back to the merchant...
                      </div>
                    </li>
                  )}

                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentInformation;
